import React from 'react';
import { t } from '@citrite/translate';
import {
	Button,
	CloseIcon,
	layout,
	Modal,
	ModalBody,
	ModalProps,
	ModalTitle,
	palette,
	typography,
} from '@citrite/web-ui-component';
// eslint-disable-next-line @citrite/workspace-ui/no-platform-dependency-access-under-src
import { showModal } from '@citrite/workspace-ui-platform';
import styled from '@emotion/styled';
import {
	CWA_DOWNLOAD_LINK as cwaDownloadLink,
	GOOGLE_CHROME_DOWNLOAD_LINK as chromeDownloadLink,
} from 'javascript/sf/constants';

export interface LaunchInCEPDialogProps extends ModalProps {
	onContinue: () => void;
}

const StyledModalBody = styled(ModalBody)`
	padding: ${layout.largeSpace};
`;

const StyledTitle = styled(ModalTitle)`
	font-size: ${typography.sizes.xl2};
	font-weight: ${typography.weights.semiBold};
	margin-bottom: 0 !important;
	color: ${palette.grey900};
`;

const StyledButton = styled(Button.Primary)`
	margin: ${layout.smallSpace} 0 ${layout.mediumSpace} 0;
`;

const StyledCloseIcon = styled(CloseIcon)`
	cursor: pointer;
`;

const StyledTitleSection = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: calc(${layout.mediumSpace});
	color: ${palette.grey900};
`;

const StyledHorizontalLine = styled.hr`
	margin: ${layout.mediumSpace} 0;
`;

const StyledParagraph = styled.p`
	margin: ${layout.smallSpace} 0;
`;

const openLinkInNewTab = (url: string) => window.open(url, '_blank');

const SaasLaunchInCEPDialog = ({ onContinue, ...props }: LaunchInCEPDialogProps) => {
	const onChromeDownload = () => openLinkInNewTab(chromeDownloadLink);
	const onCwaDownload = () => openLinkInNewTab(cwaDownloadLink);

	return (
		<Modal {...props} closeOnOverlayClick closeOnEscape showCloseIcon>
			<StyledModalBody>
				<StyledTitleSection>
					<StyledTitle showCloseIcon>{t('Workspace:cep_handler.title')}</StyledTitle>
					<StyledCloseIcon size={16} onClick={props.onDismiss} />
				</StyledTitleSection>
				<StyledParagraph style={{ margin: '0 0 32px' }}>
					{t('Workspace:cep_handler.details1')}
				</StyledParagraph>

				<StyledParagraph
					dangerouslySetInnerHTML={{
						__html: t('Workspace:cep_handler.details2'),
					}}
				/>
				<StyledButton onClick={onContinue} data-testid="launch-button">
					{t('Workspace:cep_handler.launch_button_text')}
				</StyledButton>
				<StyledHorizontalLine />
				<StyledParagraph>
					{t('Workspace:cep_handler.verify_chrome_text')}
					<Button.TextPrimary
						type="button"
						onClick={onChromeDownload}
						data-testid="chrome-download"
					>
						{t(
							'Workspace:citrix_enterprise_browser.saasAppLaunch.modal.troubleShooting.download'
						)}
					</Button.TextPrimary>
				</StyledParagraph>
				<StyledParagraph>
					{t('Workspace:cep_handler.verify_chrome_enterprise_logged_in_text')}
				</StyledParagraph>
				<StyledParagraph>
					{t('Workspace:cep_handler.verify_cwa_text')}
					<Button.TextPrimary
						type="button"
						onClick={onCwaDownload}
						data-testid="cwa-download"
					>
						{t(
							'Workspace:citrix_enterprise_browser.saasAppLaunch.modal.troubleShooting.download'
						)}
					</Button.TextPrimary>
				</StyledParagraph>
				<StyledParagraph>{t('Workspace:cep_handler.contact_admin')}</StyledParagraph>
			</StyledModalBody>
		</Modal>
	);
};

export const showSaasLaunchInCEPDialog = (onContinue: () => void) =>
	showModal(<SaasLaunchInCEPDialog onContinue={onContinue} />);
